"use client";

import React from "react";
import { Activity, Cpu, Database, Eye, Layers, Network, SlidersHorizontal, ArrowRight, CircleCheck as CheckCircle2, PhoneCall } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface CapabilityItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
  link: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const capabilities: CapabilityItem[] = [{
  id: "ft-nir",
  title: "FT-NIR Process Analysis",
  subtitle: "Real-Time Inline Spectroscopy",
  description: "High-precision Fourier-transform near-infrared instrumentation designed for real-time quantitative monitoring of blends, moisture, granulation, and drying cycles.",
  highlights: ["Multiplexed fiber optics", "Non-destructive testing", "High signal-to-noise ratio"],
  link: "/NirProcessSpectrometersPage",
  icon: Activity
}, {
  id: "process-raman",
  title: "Process Raman",
  subtitle: "High-Throughput Optical Sensing",
  description: "Continuous chemical fingerprinting and reaction tracking for biopharma upstream fermentation, downstream purification, and active pharmaceutical crystallization.",
  highlights: ["Aqueous phase tolerance", "Non-invasive glass/polymer sampling", "Rapid multi-channel acquisition"],
  link: "/ProcessAnalyticalTechnologyPage",
  icon: Eye
}, {
  id: "raman-probes",
  title: "Raman Probes & Sampling",
  subtitle: "Harsh Environment Optics",
  description: "Industrial-grade immersion probes, non-contact zoom heads, and flow cells engineered to withstand extreme pressures, high temperatures, and corrosive media.",
  highlights: ["Hastelloy & Sapphire construction", "ATEX / Class I Div 1 options", "Clean-in-place (CIP/SIP) design"],
  link: "/ProcessAnalyticalTechnologyPage",
  icon: Layers
}, {
  id: "ftnir-probes",
  title: "FT-NIR Probes & Sampling",
  subtitle: "Versatile Transmission & Reflectance",
  description: "Diffuse reflectance probes, transflectance flow cells, and automated turret accessories tailored for seamless integration into manufacturing lines.",
  highlights: ["Sanitary Tri-Clamp fittings", "Variable optical path lengths", "Purged and pressurized housings"],
  link: "/NirProcessSpectrometersPage",
  icon: SlidersHorizontal
}, {
  id: "pat-software",
  title: "PAT Software",
  subtitle: "Process Orchestration & Compliance",
  description: "Centralized Process Analytical Technology platforms that synchronize analyzer data, multivariate models, and continuous quality verification workflows.",
  highlights: ["21 CFR Part 11 compliant", "Automated audit trailing", "Real-time critical quality attributes (CQAs)"],
  link: "/ProcessAnalyticalTechnologyPage",
  icon: Database
}, {
  id: "chemometrics",
  title: "Chemometrics",
  subtitle: "Multivariate Calibration & Modeling",
  description: "Specialized quantitative and qualitative method development, including PLS regression, PCA classification, model transfer, and routine lifecycle validation.",
  highlights: ["Robust outlier diagnostics", "Method transfer protocols", "Full regulatory documentation"],
  link: "/FeasibilityStudiesPage",
  icon: Cpu
}, {
  id: "plc-dcs-integration",
  title: "PLC / DCS Integration",
  subtitle: "Industrial Network Automation",
  description: "Direct deterministic communication between analytical engines and site control systems for automated closed-loop feedback and batch control.",
  highlights: ["OPC UA, Modbus & Profibus", "Direct SCADA/DCS handshake", "Fail-safe automated triggers"],
  link: "/DiscussYourApplicationPage",
  icon: Network
}];
export default function Capabilities() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };
  return <section id="keycapabilities" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border/40">
      <div className="max-w-[1184px] mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1">
            End-to-End PAT Solutions
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Key Analytical Capabilities
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            From optical sensor integration to compliant multivariate automation, we deliver comprehensive
            PAT infrastructure for regulated pharmaceutical, biopharmaceutical, and chemical manufacturing.
          </p>
        </div>

        {/* 7 Capabilities Grid + 8th Consultation Action */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {capabilities.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={index} variants={cardVariants} className="flex">
                <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center justify-center size-12 rounded-lg bg-secondary text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <span className="text-xs font-mono font-medium text-muted-foreground">
                        0{index + 1}
                      </span>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {item.title}
                      </CardTitle>
                      <p className="text-xs font-medium text-primary mt-1">
                        {item.subtitle}
                      </p>
                    </div>
                    <CardDescription className="text-sm text-muted-foreground leading-normal">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 pb-6 flex-grow">
                    <div className="border-t border-border/60 pt-4 mt-2">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                        Technical Highlights
                      </p>
                      <ul className="space-y-1.5 text-xs text-muted-foreground">
                        {item.highlights.map((highlight, hIndex) => <li key={hIndex} className="flex items-center gap-2">
                            <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                            <span>{highlight}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0 border-t border-border/40 flex items-center justify-between">
                    <Button variant="link" className="px-0 h-auto font-semibold text-sm text-primary group" asChild>
                      <Link to={item.link} className="inline-flex items-center gap-1.5">
                        <span>Explore Technology</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}

          {/* 8th Card: Direct Engineering Support CTA Card */}
          <motion.div data-index={7} variants={cardVariants} className="flex">
            <Card className="flex flex-col justify-between w-full rounded-xl border border-primary/30 bg-secondary/50 shadow-sm p-2 text-foreground">
              <CardHeader className="space-y-3 pb-4">
                <div className="flex items-center justify-center size-12 rounded-lg bg-primary text-primary-foreground">
                  <PhoneCall className="size-6" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold font-default text-foreground">
                    Custom Application Support
                  </CardTitle>
                  <p className="text-xs font-medium text-primary mt-1">
                    Engineering & Qualification
                  </p>
                </div>
                <CardDescription className="text-sm text-muted-foreground leading-normal">
                  Need assistance matching optical probes, spectrometers, and communication protocols to your specific process vessels?
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-0 pb-6 flex-grow">
                <div className="border-t border-border/60 pt-4 mt-2 space-y-2 text-xs text-muted-foreground">
                  <p className="text-foreground font-medium">
                    Our application engineers evaluate your process matrices and provide on-site feasibility testing across Ireland and the UK.
                  </p>
                </div>
              </CardContent>

              <CardFooter className="pt-0 border-t border-border/40">
                <Button className="w-full font-medium" asChild>
                  <Link to="/DiscussYourApplicationPage" className="inline-flex items-center justify-center gap-2">
                    <span>Discuss Your Application</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}